import { ReactNode } from "react";

export type PasswordRequirement = {
  id: string;
  message: string;
  isMet: boolean;
  hasError: boolean;
};

export type Props = {
  /** Current password value */
  password: string;
  /** Whether password is too short (error state) */
  isTooShortError: boolean;
  /** Whether password is missing special char (error state) */
  isInvalidStringError: boolean;
  /** Layout direction */
  layout?: "horizontal" | "vertical";
  /** Render function for each requirement */
  renderRequirement: (requirement: PasswordRequirement) => ReactNode;
  /** Container className */
  className?: string;
};

const PASSWORD_MIN_LENGTH = 8;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*(),.?":{}|<>]/;

/**
 * Base password requirements component - unstyled, uses render props for flexibility.
 * Wrap with styled version in shared/features/auth.
 */
export function PasswordRequirementsBase({
  password,
  isTooShortError,
  isInvalidStringError,
  renderRequirement,
  className,
}: Props) {
  const isLongEnough = password?.length >= PASSWORD_MIN_LENGTH;
  const hasSpecialChar = SPECIAL_CHAR_REGEX.test(password || "");

  const requirements: PasswordRequirement[] = [
    {
      id: "length",
      message: "Must be at least 8 characters long",
      isMet: isLongEnough,
      hasError: isTooShortError,
    },
    {
      id: "special",
      message: "Must contain a special character",
      isMet: hasSpecialChar,
      hasError: isInvalidStringError,
    },
  ];

  return (
    <div className={className} data-testid="password-requirements">
      {requirements.map((requirement) => renderRequirement(requirement))}
    </div>
  );
}

export default PasswordRequirementsBase;

